import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { useAtom } from 'jotai';
import { isObject, sortBy } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import { PAGE_SIZES } from '@/constants/common';
import {
  filteredRulesAtom,
  rulesDetailsAtom,
  rulesDetailsExpandedAtom,
  rulesTableOrderAtom,
  rulesTableOrderByAtom,
  rulesTablePageAtom,
  rulesTableRowsAtom
} from '@/pages/Administration/AdministrationRules/AdministrationRules.store';
import type {
  SensorRuleFileDTO,
  SensorRuleFileKeys
} from '@/pages/Administration/AdministrationRules/AdministrationRules.types';
import { selectedOrganizationIdAtom } from '@/pages/Organizations';

import { COLUMNS } from './RulesTable.constants';
import useStyles from './RulesTable.styles';

const RulesTable = () => {
  const [filteredRules] = useAtom(filteredRulesAtom);
  const [ruleDetails, setRulesDetails] = useAtom(rulesDetailsAtom);
  const [, setAreRulesDetailsExpanded] = useAtom(rulesDetailsExpandedAtom);
  const [order, setOrder] = useAtom(rulesTableOrderAtom);
  const [orderBy, setOrderBy] = useAtom(rulesTableOrderByAtom);
  const [page, setPage] = useAtom(rulesTablePageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(rulesTableRowsAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const ref = useRef<HTMLTableSectionElement>(null);
  const { classes } = useStyles();
  const theme = useTheme();

  // Reset to first page when list of rules changes, for example when filters are applied
  useEffect(() => setPage(0), [filteredRules.length, setPage]);

  const handleClick = (row: SensorRuleFileDTO) => {
    setRulesDetails(row);
    setAreRulesDetailsExpanded(true);
  };

  const handleChangePage = (
    _event: React.MouseEvent | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPage(page);
    setRowsPerPage(Number(event.target.value));
  };

  const handleSort = (property: string) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const visibleRows = useMemo(() => {
    let orderProperty = orderBy;
    if (isObject(filteredRules?.[0]?.[orderBy as SensorRuleFileKeys])) {
      orderProperty = `${orderBy}.name`;
    }

    let result = sortBy(filteredRules, orderProperty);
    if (order === 'desc') {
      result = result.reverse();
    }
    result = result.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return result;
  }, [order, orderBy, page, rowsPerPage, filteredRules]);

  return (
    <TableContainer ref={ref} className={classes.rulesTableContainer}>
      {filteredRules.length === 0 && (
        <Box className={classes.noData}>No data</Box>
      )}
      <Table stickyHeader className={classes.rulesTable}>
        <TableHead>
          <TableRow>
            {COLUMNS.map(
              (column, index) =>
                (!column.hidden ||
                  column.hidden(selectedOrganizationId as number)) && (
                  <TableCell
                    align="left"
                    style={{
                      width: column.width,
                      paddingLeft: index === 0 ? 16 : ''
                    }}
                    className={classes.rulesTableHeaderCell}
                    key={column.id}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow
              key={row.id}
              sx={{
                cursor: 'pointer',
                backgroundColor:
                  row.id === ruleDetails?.id
                    ? theme.vars.palette.transparent.a5
                    : 'inherit',
                '&:hover': {
                  backgroundColor: theme.vars.palette.transparent.a10
                }
              }}
            >
              {COLUMNS.map(
                (column, colIndex) =>
                  (!column.hidden ||
                    column.hidden(selectedOrganizationId as number)) && (
                    <TableCell
                      className={classes.tableBodyCell}
                      align="left"
                      key={column.id}
                      style={{
                        width: column.width,
                        paddingLeft: colIndex === 0 ? 16 : ''
                      }}
                    >
                      {column.cell(row as SensorRuleFileDTO, () =>
                        handleClick(row as SensorRuleFileDTO)
                      )}
                    </TableCell>
                  )
              )}
            </TableRow>
          ))}

          {/* empty row to fill the table height */}
          <TableRow sx={{ height: '100%' }}>
            <TableCell colSpan={7} sx={{ padding: 0, border: 'none' }} />
          </TableRow>
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              className={classes.rulesTablePagination}
              rowsPerPageOptions={PAGE_SIZES}
              count={filteredRules.length}
              rowsPerPage={rowsPerPage}
              page={rowsPerPage * page > filteredRules.length ? 0 : page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default RulesTable;
